import * as PIXI from 'pixi.js';
import { soundManager } from './soundManager';
import { languageManager } from './languageManager';
import { networkData } from './data';
import { handleScratchScore } from './networkService';
const {dataLastPendingBet} = networkData;

export class SymbolManager {

  constructor(app, sheet, frame = null, basegameSheet = null, matchingIconTxtSheet = null) {
    this.app = app;
    this.sheet = sheet;
    this.basegameSheet = basegameSheet;
    this.matchingIconTxtSheet = matchingIconTxtSheet;
    this.frame = frame;
    this.symbols = [];
    this.matchingIcon = null;
    this.symbolsRevealingEnabled = false;
    this.data=[];
    
    // Listen for language changes
    languageManager.addListener(() => this.updateLanguage());
  }

  enableSymbolRevealing() {
    this.symbolsRevealingEnabled = true;
  }

  _updateScales() {
    const screenWidth = this.app.screen.width;
  
    this.symbolSpacingX = 120;
    this.symbolSpacingY = 120;
    this.extraOffset = 100;
    this.gridSlotBaseScale = 0.6; // Base scale for the 3x3 grid containers
    this.actualSymbolVisualRatio = 1.0; // Ratio for actual symbol sprite relative to its container
    this.sideSymbolScale = 0.6; // Scale for the left/right symbols and their covers
  
    if (screenWidth <= 490) { // Small mobile
      this.symbolSpacingX = 65;
      this.symbolSpacingY = 65;
      this.extraOffset = 60;
      this.gridSlotBaseScale = 0.35;
      this.actualSymbolVisualRatio = 0.8; // Actual symbol will be 80% of its container size
      this.sideSymbolScale = 0.34;
    } else if (screenWidth <= 767) { // Regular mobile
      this.symbolSpacingX = 80;
      this.symbolSpacingY = 80;
      this.extraOffset = 70;
      this.gridSlotBaseScale = 0.45;
      this.actualSymbolVisualRatio = 0.85; // Actual symbol will be 85% of its container size
      this.sideSymbolScale = 0.44;
    } else if (screenWidth <= 1024) {
      this.symbolSpacingX = 100;
      this.symbolSpacingY = 100;
      this.extraOffset = 80;
      this.gridSlotBaseScale = 0.5;
      this.actualSymbolVisualRatio = 1.0;
      this.sideSymbolScale = 0.5;
    } else {
      this.gridSlotBaseScale = 0.6;
      this.actualSymbolVisualRatio = 1.0;
      this.sideSymbolScale = 0.6;
    }
  }
  

  createGrid() {
    const rows = 3;
    const cols = 3;
    
    // Mobile-responsive spacing and scaling
    const screenWidth = this.app.screen.width;
    let symbolSpacingX = 120;
    let symbolSpacingY = 120;
    let extraOffset = 100;
    let symbolScale = 0.6;
    
    
    if (screenWidth <= 480) {
      symbolSpacingX = 65;
      symbolSpacingY = 65;
      extraOffset = 60;
      symbolScale = 0.34; // Slightly smaller on small mobile
    } else if (screenWidth <= 767) {
      symbolSpacingX = 80;
      symbolSpacingY = 80;
      extraOffset = 70;
      symbolScale = 0.44; // Slightly smaller on regular mobile
    } else if (screenWidth <= 1024) {
      symbolSpacingX = 100;
      symbolSpacingY = 100;
      extraOffset = 80;
      symbolScale = 0.5;
    }
    
    const symbolTexture = this.sheet.textures['symbol_base.png'];

    let centerX, centerY;
    if (this.frame) {

      this.frame.x = this.app.screen.width / 2;
      this.frame.y = this.app.screen.height / 2;
      centerX = this.frame.x;
      centerY = this.frame.y - 50 + (this.app.screen.width <= 480 ? 25 : this.app.screen.width <= 767 ? 25 : 0); // Move grid down only on mobile
    } else {
      centerX = this.app.screen.width / 2;
      centerY = this.app.screen.height / 2 + (this.app.screen.width <= 480 ? 30 : this.app.screen.width <= 767 ? 25 : 0); // Move grid down only on mobile
    }
    // Baseline (without extra grid offset) for side covers
    const sideCenterY = this.frame ? this.frame.y - 50 : this.app.screen.height / 2;

    // Place 3x3 grid
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const symbol = new PIXI.Sprite(symbolTexture);
        symbol.anchor.set(0.5);
        symbol.scale.set(symbolScale);
        symbol.x = centerX + (col - 1) * symbolSpacingX;
        symbol.y = centerY + (row - 1) * symbolSpacingY;
        symbol.interactive = true;
        symbol.cursor = 'pointer';
        this.app.stage.addChild(symbol);
        this.symbols.push(symbol);
      }
    }
    // Extra symbol on the left
    const leftSymbol = new PIXI.Sprite(symbolTexture);
    leftSymbol.anchor.set(0.5);
    leftSymbol.scale.set(symbolScale);
    leftSymbol.x = centerX - (cols / 2) * symbolSpacingX - extraOffset + (screenWidth <= 480 ? 2 : screenWidth <= 767 ? 8 : screenWidth <= 1024 ? -5 : 0);
    leftSymbol.y = sideCenterY - 9 + (screenWidth <= 480 ? 26 : screenWidth <= 767 ? 10 : screenWidth <= 1024 ? 10 : 0); // Move down
    leftSymbol.interactive = true;
    leftSymbol.cursor = 'pointer';
    this.app.stage.addChild(leftSymbol);
    this.symbols.push(leftSymbol);

    // Extra symbol on the right
    const rightSymbol = new PIXI.Sprite(symbolTexture);
    rightSymbol.anchor.set(0.5);
    rightSymbol.scale.set(symbolScale);
    rightSymbol.x = centerX + (cols / 2) * symbolSpacingX + extraOffset - (screenWidth <= 480 ? 0 : screenWidth <= 767 ? 15 : screenWidth <= 1024 ? -5 : 0); // Move towards inside
    rightSymbol.y = sideCenterY - 9 + (screenWidth <= 480 ? 26 : screenWidth <= 767 ? 10 : screenWidth <= 1024 ? 10 : 0); // Move down
    rightSymbol.interactive = true;
    rightSymbol.cursor = 'pointer';
    this.app.stage.addChild(rightSymbol);
    this.symbols.push(rightSymbol);

    // Remove old matching icons if they exist
    if (this.matchingIcons && Array.isArray(this.matchingIcons)) {
      for (const icon of this.matchingIcons) {
        if (icon && icon.parent) {
          icon.parent.removeChild(icon);
        }
      }
      this.matchingIcons = [];
    }

    if (this.basegameSheet && this.basegameSheet.textures['matching_icon_base.png']) {
      this.matchingIcons = [];
      
      // Mobile-responsive scaling for matching icons
      let iconScale = 0.65;
      let offsetY = 110;
      let fontSize = 28;
      
      if (screenWidth <= 480) {
        iconScale = 0.25; // Much smaller for small mobile
        offsetY = 60; // Position below the covers (increased from 40)
        fontSize = 28; // Increased from 14 to 20 for better visibility
      } else if (screenWidth <= 767) {
        iconScale = 0.35; // Smaller for regular mobile
        offsetY = 80; // Position below the covers (increased from 60)
        fontSize = 24; // Increased from 18 to 24 for better visibility
      } else if (screenWidth <= 1024) {
        iconScale = 0.5; // Medium for tablet
        offsetY = 100; // Position below the covers (increased from 80)
        fontSize = 26; // Increased from 22 to 26 for better visibility
      }
      
      // Left
      const leftIcon = new PIXI.Sprite(this.basegameSheet.textures['matching_icon_base.png']);
      leftIcon.anchor.set(0.5);
      leftIcon.x = leftSymbol.x;
      leftIcon.y = leftSymbol.y + offsetY;
      leftIcon.scale.set(iconScale);
      this.app.stage.addChild(leftIcon);
      this.matchingIcons.push(leftIcon);
      // Right
      const rightIcon = new PIXI.Sprite(this.basegameSheet.textures['matching_icon_base.png']);
      rightIcon.anchor.set(0.5);
      rightIcon.x = rightSymbol.x;
      rightIcon.y = rightSymbol.y + offsetY;
      rightIcon.scale.set(iconScale);
      this.app.stage.addChild(rightIcon);
      this.matchingIcons.push(rightIcon);


      const matchingIconTextStyle = {
        align: 'center',
        dropShadow: true,
        dropShadowAngle: 1.5,
        dropShadowDistance: 2,
        fill: '#c3bd94',
        fontFamily: 'TCCEB',
        fontSize: fontSize,
        fontWeight: 'bolder',
        lineJoin: 'round',
        miterLimit: 0,
        padding: 20,
        stroke: '#3a4124',
        strokeThickness: 4,
        whiteSpace: 'normal',
      };

      // Get current language to adjust font size
      const currentLanguage = languageManager.getCurrentLanguage();
      const adjustedFontSize = this.getMatchingIconsFontSize(currentLanguage, fontSize);
      
      const leftText = new PIXI.Text(languageManager.getText('matchingIcons'), {
        ...matchingIconTextStyle,
        fontSize: adjustedFontSize
      });
      leftText.anchor.set(0.5);
      leftText.x = 0;
      leftText.y = 0;
      leftIcon.addChild(leftText);
      this.leftMatchingText = leftText;

      const rightText = new PIXI.Text(languageManager.getText('matchingIcons'), {
        ...matchingIconTextStyle,
        fontSize: adjustedFontSize
      });
      rightText.anchor.set(0.5);
      rightText.x = 0;
      rightText.y = 0;
      rightIcon.addChild(rightText);
      this.rightMatchingText = rightText;
    }
  }

  /**
   * This function Adds the 11 actual symbols (excluding the first/cover symbol) in random order
   * to all 11 grid positions, placing them just below the covers.
   * Call this after createGrid().
   */
  addActualSymbols(gameData) {
    const { scores = [], baseScorePattern = [], scratchedScoresIndexes = [] } = gameData || {};
    this.symbolsRevealingEnabled = true;

    // --- Basic validation ---
    if (!this.sheet || !this.sheet.textures) {
      console.error("[addActualSymbols] PIXI sheet or textures not available.");
      return;
    }
    if (!scores || scores.length === 0) {
      console.error("[addActualSymbols] Invalid game data: 'scores' array is empty or undefined. Cannot add actual symbols.");
      return;
    }

    // Gather all available visual (static) symbol keys
    const allStaticSymbolKeys = Object.keys(this.sheet.textures)
      .filter(key => key.endsWith('_static.png') && key !== 'symbol_base.png');

    console.log(`[addActualSymbols] Found ${allStaticSymbolKeys.length} _static symbol textures.`);

    const drawRandomAndRemove = (arr) => {
      if (!arr || arr.length === 0) return null;
      const idx = Math.floor(Math.random() * arr.length);
      return arr.splice(idx, 1)[0];
    };

    const pickRandom = (arr) => (arr && arr.length ? arr[Math.floor(Math.random() * arr.length)] : null);

    if (allStaticSymbolKeys.length === 0) {
      console.error('[addActualSymbols] No _static symbol textures were found in the sprite sheet. Aborting.');
      return;
    }

    const overlapIndices = [];
    if (Array.isArray(baseScorePattern) && baseScorePattern.length > 0) {
      // cap to 9 since grid has 9 positions
      const limit = Math.min(scores.length, 9);
      for (let i = 0; i < limit; i++) {
        if (baseScorePattern.includes(scores[i])) overlapIndices.push(i);
      }
    }

    const isWinningGameLocally = overlapIndices.length > 0;
    console.log('[addActualSymbols] isWinningGameLocally:', isWinningGameLocally, 'overlapIndices:', overlapIndices);

    // Prepare final arrays
    const finalGridSymbolKeys = new Array(9).fill(null);
    const finalMatchingSymbolKeys = new Array(2).fill(null);

    let pool = [...allStaticSymbolKeys];
    const usedKeys = new Set();

    // --- LOSING GAME: ensure 11 distinct visuals if possible ---
    if (!isWinningGameLocally) {
      console.log('[addActualSymbols] Executing losing-game assignment (prefer 11 unique symbols).');
      if (pool.length >= 11) {
        for (let i = 0; i < 11; i++) {
          const k = drawRandomAndRemove(pool);
          if (k) {
            if (i < 9) finalGridSymbolKeys[i] = k;
            else finalMatchingSymbolKeys[i - 9] = k;
            usedKeys.add(k);
          }
        }
      } else {
        // Not enough unique textures: we still try to avoid duplicates as much as possible
        // 1) take all unique keys
        const allUnique = [...allStaticSymbolKeys];
        // 2) shuffle selection by repeatedly picking a random unused key when possible
        while (usedKeys.size < Math.min(11, allUnique.length)) {
          const candidate = drawRandomAndRemove(allUnique);
          if (candidate) usedKeys.add(candidate);
        }
        // If after exhausting unique keys we still need more (because textures < 11), allow repeats
        while (usedKeys.size < 11) {
          // try to pick a key not already used if possible
          const remaining = allStaticSymbolKeys.filter(k => !usedKeys.has(k));
          if (remaining.length > 0) usedKeys.add(pickRandom(remaining));
          else usedKeys.add(pickRandom(allStaticSymbolKeys));
        }
        // Assign the selected 11 keys into arrays
        const selected = Array.from(usedKeys).slice(0, 11);
        for (let i = 0; i < 9; i++) finalGridSymbolKeys[i] = selected[i];
        finalMatchingSymbolKeys[0] = selected[9];
        finalMatchingSymbolKeys[1] = selected[10];
      }
    }
    // --- WINNING GAME: ensure visuals in grid indices listed in overlapIndices are the ones matched ---
    else {
      console.log('[addActualSymbols] Executing winning-game assignment (make matching visuals align with overlap indices).');

      const leftBaseValue = baseScorePattern[0];
      const rightBaseValue = baseScorePattern[1];

      const leftOverlapIdx = scores.findIndex(score => score === leftBaseValue);
      const rightOverlapIdx = scores.findIndex(score => score === rightBaseValue);

      // 1) Reserve visuals for matching grid indices
      [leftOverlapIdx, rightOverlapIdx].forEach(idx => {
        if (idx !== -1 && idx < 9) {
          const chosen = drawRandomAndRemove(pool) || pickRandom(allStaticSymbolKeys);
          finalGridSymbolKeys[idx] = chosen;
          usedKeys.add(chosen);
        }
      });

      // 2) Fill remaining grid slots
      for (let i = 0; i < 9; i++) {
        if (finalGridSymbolKeys[i]) continue;
        const chosen = drawRandomAndRemove(pool) || pickRandom(allStaticSymbolKeys);
        if (usedKeys.has(chosen)) {
          const alt = pool.find(k => !usedKeys.has(k));
          if (alt) {
            const idx = pool.indexOf(alt);
            if (idx >= 0) pool.splice(idx, 1);
            finalGridSymbolKeys[i] = alt;
            usedKeys.add(alt);
            continue;
          }
        }
        finalGridSymbolKeys[i] = chosen;
        usedKeys.add(chosen);
      }

      // 3) Explicit left/right symbol mapping
      if (leftOverlapIdx !== -1) {
        finalMatchingSymbolKeys[0] = finalGridSymbolKeys[leftOverlapIdx];
      }
      if (rightOverlapIdx !== -1) {
        finalMatchingSymbolKeys[1] = finalGridSymbolKeys[rightOverlapIdx];
      }

      // 4) Fallbacks
      if (!finalMatchingSymbolKeys[0]) {
        finalMatchingSymbolKeys[0] = drawRandomAndRemove(pool) || pickRandom(allStaticSymbolKeys);
      }
      if (!finalMatchingSymbolKeys[1]) {
        const remainingChoices = allStaticSymbolKeys.filter(k => k !== finalMatchingSymbolKeys[0]);
        finalMatchingSymbolKeys[1] = drawRandomAndRemove(pool) || pickRandom(remainingChoices) || finalMatchingSymbolKeys[0];
      }
    }

    // --- Final safety: ensure arrays are fully populated ---
    for (let i = 0; i < 9; i++) if (!finalGridSymbolKeys[i]) finalGridSymbolKeys[i] = pickRandom(allStaticSymbolKeys);
    for (let i = 0; i < 2; i++) if (!finalMatchingSymbolKeys[i]) finalMatchingSymbolKeys[i] = pickRandom(allStaticSymbolKeys);

    console.log('[addActualSymbols] finalGridSymbolKeys:', finalGridSymbolKeys);
    console.log('[addActualSymbols] finalMatchingSymbolKeys:', finalMatchingSymbolKeys);

    // --- Prepare SpineObject once (avoid require in the inner loop) ---
    const { SpineObject } = require('./SpineObject');

    // --- SYMBOL PLACEMENT LOOP (keeps your existing rendering/reveal logic, but uses the robust assignment above) ---
    for (let i = 0; i < this.symbols.length; i++) {
      if (this.symbols[i] && this.symbols[i].parent === this.app.stage) {
        this.app.stage.removeChild(this.symbols[i]);
      }

      const symbolContainer = new PIXI.Container();
      symbolContainer.x = this.symbols[i] ? this.symbols[i].x : 0;
      symbolContainer.y = this.symbols[i] ? this.symbols[i].y : 0;
      let actualSymbol = null;
      let symbolKeyToAssign = null;
      let amountValue = '';
      let rawScoreValue = undefined;

      if (i < 9) {
        symbolKeyToAssign = finalGridSymbolKeys[i];
        rawScoreValue = scores[i];
        amountValue = rawScoreValue ?? '';
      } else {
        symbolKeyToAssign = finalMatchingSymbolKeys[i - 9];
        amountValue = '';
      }

      // Create visual sprite if texture exists
      if (symbolKeyToAssign && this.sheet.textures[symbolKeyToAssign]) {
        actualSymbol = new PIXI.Sprite(this.sheet.textures[symbolKeyToAssign]);
        actualSymbol.anchor.set(0.5);
        actualSymbol.zIndex = 100;
        if (this.symbols[i]) actualSymbol.scale.set(this.symbols[i].scale.x, this.symbols[i].scale.y);
        actualSymbol.x = 0;
        actualSymbol.y = 0;
        actualSymbol._originalScale = this.symbols[i] ? { x: this.symbols[i].scale.x, y: this.symbols[i].scale.y } : { x: 1, y: 1 };
        symbolContainer.addChild(actualSymbol);
      } else {
        console.warn(`[addActualSymbols] No texture for key ${symbolKeyToAssign} at index ${i}`);
      }

      symbolContainer.symbolKey = symbolKeyToAssign;

      // Amount text (only for grid positions)
      if (i < 9) {
        const screenWidth = this.app.screen.width;
        let amountFontSize = 16;
        let amountYOffset = 38;
        if (screenWidth <= 490) {
          amountFontSize = 10;
          amountYOffset = 12;
        } else if (screenWidth <= 767) {
          amountFontSize = 14;
          amountYOffset = 28;
        }
        const amountText = new PIXI.Text(`$${amountValue}`, {
          fontFamily: 'TCCEB',
          fontSize: amountFontSize,
          fontWeight: 'bolder',
          fill: '#fffbe0',
          stroke: '#3a4124',
          strokeThickness: 4,
          align: 'center',
        });
        amountText.anchor.set(0.5, 0);
        amountText.x = 0;
        amountText.y = amountYOffset;
        amountText.visible = true;
        symbolContainer.addChild(amountText);
        symbolContainer.amountText = amountText;
      }

      // Create cover spine and attach reveal logic (keeps your original approach)
      const coverSpine = new SpineObject('reveal_fxSkeleton', 'reveal_fxAtlas', 0, 0);
      const screenWidthForCover = this.app.screen.width;
      let coverScaleFactor = 1.05;
      if (screenWidthForCover <= 480) coverScaleFactor = 1;
      else if (screenWidthForCover <= 767) coverScaleFactor = 1.01;
      else if (screenWidthForCover <= 1024) coverScaleFactor = 1.02;
      coverSpine.zIndex = 100;
      coverSpine.spine.scale.set((this.symbols[i] ? this.symbols[i].scale.x : 1) * coverScaleFactor, (this.symbols[i] ? this.symbols[i].scale.y : 1) * coverScaleFactor);
      if (coverSpine.spine.anchor) coverSpine.spine.anchor.set(0.5, 0.5);
      coverSpine.x = 0;
      coverSpine.y = 0;
      const animations = coverSpine.spine.state.data.skeletonData.animations.map(a => a.name);
      let playAnim = animations.includes('reveal') ? 'reveal' : animations[0];

      coverSpine.interactive = true;
      coverSpine.cursor = 'pointer';
      symbolContainer.coverSpine = coverSpine;
      symbolContainer.interactive = true;
      symbolContainer.buttonMode = true;

      if (scratchedScoresIndexes.includes(i)) {
        symbolContainer.isRevealed = true;
        if (symbolContainer.amountText) symbolContainer.amountText.visible = true;
        coverSpine.visible = false;
        symbolContainer.interactive = false;
        symbolContainer.buttonMode = false;
      } else {
        symbolContainer.on('pointertap', async () => {
          if (this.symbolsRevealingEnabled && !symbolContainer.isRevealed) {
            symbolContainer.interactive = false;
            symbolContainer.buttonMode = false;
            try {
              const scratchResponse = await handleScratchScore(i);
              soundManager.playReveal();
              coverSpine.spine.state.setAnimation(0, playAnim, false);
              if (symbolContainer.amountText && i < 9) symbolContainer.amountText.visible = true;
              symbolContainer.isRevealed = true;
              const listener = {
                complete: (trackEntry) => {
                  if (trackEntry.animation.name === playAnim) {
                    coverSpine.visible = false;
                    coverSpine.spine.state.removeListener(listener);
                    this.checkAndHighlightWin(scratchResponse);
                  }
                }
              };
              coverSpine.spine.state.addListener(listener);
            } catch (err) {
              console.error(`Failed to process scratch for symbol index ${i}:`, err);
              symbolContainer.interactive = true;
              symbolContainer.buttonMode = true;
            }
          }
        });
      }

      symbolContainer.addChild(coverSpine);
      symbolContainer.actualSymbol = actualSymbol;
      this.app.stage.addChild(symbolContainer);
      this.symbols[i] = symbolContainer;
    }
  }
  
  /**
   * Reveals all symbols based on the final API response from a 'reveal all' call.
   * This method ensures all covers are hidden and values are displayed immediately,
   * and then triggers the final win/loss highlight.
   * @param {object} apiResponse The full API response from the 'reveal all' endpoint.
   */
  async revealAllSymbolsFromAPIResponse(apiResponse) {
    console.log("[SymbolManager] Revealing all symbols from API response.", apiResponse);
    this.symbolsRevealingEnabled = false; // Disable further individual scratching

    // const gameResult = apiResponse?.data?.gameResult;
    // Correctly extract scores and baseScorePattern from the reveal-all API response itself
    const gameResult = apiResponse?.data?.gameResult;
    const gameResultFromRevealAll = apiResponse?.data?.gameResult;
    const scoresForDisplay = gameResultFromRevealAll?.scores || []; 
    const baseScorePatternForDisplay = gameResultFromRevealAll?.baseScorePattern || []; 

    // Use a single reveal animation for all covers, or just hide them instantly
    const revealPromises = [];

    for (let i = 0; i < this.symbols.length; i++) {
      const container = this.symbols[i];
      if (container && !container.isRevealed) { // Only process if not already revealed
        container.isRevealed = true;
        container.interactive = false;
        container.buttonMode = false;

        // Ensure the correct amount text is displayed from the API data
        let displayAmount = '';
        if (i < 9) { // Grid symbols
            displayAmount = scoresForDisplay[i] ?? '';
            if (container.amountText) {
                container.amountText.text = `$${displayAmount}`;
                container.amountText.visible = true;
            }
        } else { // Matching symbols (9,10)
          if (container.amountText) {
                container.amountText.text = ''; // Set text to empty string
                container.amountText.visible = false;
          }
        }

        if (container.coverSpine && container.coverSpine.visible) {
          const coverSpine = container.coverSpine;
          const animations = coverSpine.spine.state.data.skeletonData.animations.map(a => a.name);
          let playAnim = animations.includes('reveal') ? 'reveal' : animations[0];

          const revealPromise = new Promise(resolve => {
              // Play reveal animation
              coverSpine.spine.state.setAnimation(0, playAnim, false);
              const listener = {
                  complete: (trackEntry) => {
                      if (trackEntry.animation.name === playAnim) {
                          coverSpine.visible = false;
                          coverSpine.spine.state.removeListener(listener);
                          resolve();
                      }
                  }
              };
              coverSpine.spine.state.addListener(listener);
          });
          revealPromises.push(revealPromise);
        }
      }
    }

    // Wait for all visual reveal animations to complete
    if (revealPromises.length > 0) {
        soundManager.playReveal(); // Play reveal sound once for all
        await Promise.all(revealPromises);
    }

    // After all visuals are updated, trigger the final win/loss highlighting
    this.checkAndHighlightWin(apiResponse);
  }
  

  repositionGrid(centerX, centerY) {
    this._updateScales();
    // Grid config
    if (!this.symbols || this.symbols.length !== 11) return;
    const rows = 3;
    const cols = 3;
    
    // Determine grid center based on reel frame
    if (centerX === undefined || centerY === undefined) {
      if (this.frame) {
        centerX = this.frame.x;
        centerY = this.frame.y;
      } else {
        centerX = this.app.screen.width / 2;
        centerY = this.app.screen.height / 2;
      }
    }
    // Baseline (without extra grid offset) for side covers
    const sideCenterY = centerY - 50;
    const gridCenterY = centerY - 50 + (this.app.screen.width <= 480 ? 25 : this.app.screen.width <= 767 ? 25 : 0);

    let idx = 0;
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const symbolContainer = this.symbols[idx++];
        if (!symbolContainer) continue;
        
        // Base scale for grid slot
        symbolContainer.scale.set(this.gridSlotBaseScale);
        symbolContainer.x = centerX + (col - 1) * this.symbolSpacingX;
        symbolContainer.y = gridCenterY + (row - 1) * this.symbolSpacingY;
        
        // Actual symbol inside container
        if (symbolContainer.actualSymbol) {
          symbolContainer.actualSymbol.scale.set(this.actualSymbolVisualRatio);
          symbolContainer.actualSymbol._originalScale = { 
            x: this.actualSymbolVisualRatio, 
            y: this.actualSymbolVisualRatio 
          };
        }
    
        // Amount text responsiveness
        if (symbolContainer.amountText) {
          const screenWidth = this.app.screen.width;
          let amountFontSize = 16;
          let amountYOffset = 38;
    
          if (screenWidth <= 490) {
            amountFontSize = 10;
            amountYOffset = 12;
          } else if (screenWidth <= 768) {
            amountFontSize = 14;
            amountYOffset = 28;
          }
          
          symbolContainer.amountText.style.fontSize = amountFontSize;
          symbolContainer.amountText.y = amountYOffset;
        }
    
        // Cover spine
        if (symbolContainer.coverSpine && symbolContainer.coverSpine.spine) {
          let coverScaleFactorR = 1.05;
          symbolContainer.coverSpine.spine.scale.set(coverScaleFactorR, coverScaleFactorR);
        }
      }
    }
    
    // Extra symbol on the left
    const leftSymbol = this.symbols[idx++];
    if (leftSymbol) {
      leftSymbol.scale.set(this.sideSymbolScale);
      leftSymbol.x = centerX - (cols / 2) * this.symbolSpacingX - this.extraOffset + (this.app.screen.width <= 480 ? -4 : this.app.screen.width <= 767 ? 8 : this.app.screen.width <= 1024 ? -5 : 0);
      leftSymbol.y = sideCenterY - 9 + (this.app.screen.width <= 480 ? 26 : this.app.screen.width <= 767 ? 8 : this.app.screen.width <= 1024 ? 10 : 0);
    
      if (leftSymbol.actualSymbol) {
        leftSymbol.actualSymbol.scale.set(1);
        leftSymbol.actualSymbol._originalScale = { x: 1, y: 1 };
      }
      if (leftSymbol.coverSpine && leftSymbol.coverSpine.spine) {
        let coverScaleFactorR = 1.05;
        leftSymbol.coverSpine.spine.scale.set(coverScaleFactorR, coverScaleFactorR);
      }
    }
    
    // Extra symbol on the right
    const rightSymbol = this.symbols[idx++];
    if (rightSymbol) {
      rightSymbol.scale.set(this.sideSymbolScale);
      rightSymbol.x = centerX + (cols / 2) * this.symbolSpacingX + this.extraOffset - (this.app.screen.width <= 480 ? -4 : this.app.screen.width <= 767 ? 15 : this.app.screen.width <= 1024 ? -5 : 0);
      rightSymbol.y = sideCenterY - 9 + (this.app.screen.width <= 480 ? 26 : this.app.screen.width <= 767 ? 10 : this.app.screen.width <= 1024 ? 10 : 0);
    
      if (rightSymbol.actualSymbol) {
        rightSymbol.actualSymbol.scale.set(1);
        rightSymbol.actualSymbol._originalScale = { x: 1, y: 1 };
      }
      if (rightSymbol.coverSpine && rightSymbol.coverSpine.spine) {
        let coverScaleFactorR = 1.05;
        rightSymbol.coverSpine.spine.scale.set(coverScaleFactorR, coverScaleFactorR);
      }
    }
    
    // Matching icons responsiveness
    if (this.matchingIcons && this.matchingIcons.length === 2 && leftSymbol && rightSymbol) {
      let offsetY = 110;
      let iconScale = 0.65;
      
      if (this.app.screen.width <= 480) {
        offsetY = 60;
        iconScale = 0.25;
      } else if (this.app.screen.width <= 767) {
        offsetY = 80;
        iconScale = 0.35;
      } else if (this.app.screen.width <= 1024) {
        offsetY = 100;
        iconScale = 0.5;
      }
      
      this.matchingIcons[0].x = leftSymbol.x;
      this.matchingIcons[0].y = leftSymbol.y + offsetY;
      this.matchingIcons[0].scale.set(iconScale);
    
      this.matchingIcons[1].x = rightSymbol.x;
      this.matchingIcons[1].y = rightSymbol.y + offsetY;
      this.matchingIcons[1].scale.set(iconScale);
    }
  
  }

  // Helper to check if all covers are revealed
  allCoversRevealed() {
    return this.symbols.every(container => container.coverSpine && !container.coverSpine.visible);
  }

  // Call this after each reveal to check for win and highlight
  async checkAndHighlightWin(scratchResponse) {
    const gameResult = scratchResponse?.data?.gameResult;
    const apiResult = gameResult?.result;
  
    const scratchedScoresIndexesFromAPI = gameResult?.scratchedScoresIndexes || [];
  
    const baseScorePattern = (gameResult?.baseScorePattern || []).map(parseFloat); // Ensure numeric
    const gridScores = (gameResult?.scores || scratchResponse?.data?.mangoesValue || []).map(parseFloat);
    const scoresFromApi = (gameResult?.scores || []).map(parseFloat); // Ensure numeric
    const multiplier = gameResult?.multiplier;
    const winningAmountFromAPI = scratchResponse?.data?.winningAmount || "0";
  
    if (apiResult === "won") {
      let matchedGridIndices = [];
      let matchedMatchIndices = [];

      gridScores.forEach((scoreValue, index) => {
        // Only consider grid symbols (index < 9)
        const symbolContainer = this.symbols[index];
        if (index < 9 && baseScorePattern.includes(scoreValue)) { 
          matchedGridIndices.push(index);
        }
      });
  
      if (baseScorePattern[0] !== undefined) {
        const numericBasePattern0 = baseScorePattern[0];
        if (gridScores.includes(numericBasePattern0)) {
          matchedMatchIndices.push(0); // Index for the first matching symbol (position 9)
        }
      }
      if (baseScorePattern[1] !== undefined) {
        const numericBasePattern1 = baseScorePattern[1];
        if (gridScores.includes(numericBasePattern1)) {
          matchedMatchIndices.push(1); // Index for the second matching symbol (position 10)
        }
      }
      
      this.highlightMatches(matchedGridIndices, matchedMatchIndices);
      if (soundManager && typeof soundManager.playWin === 'function') {
        soundManager.playWin();
      }
      if (this.app.buttonManager && typeof this.app.buttonManager.showWinMessage === 'function') {
        this.app.buttonManager.showWinMessage(`$${winningAmountFromAPI}`); // Display winning amount from API
        const game = this.app.gameInstance || (window && window.gameInstance);
        if (game && typeof game?.addCredits === 'function') {
          const parsed = parseFloat(winningAmountFromAPI.replace(/[^0-9.]/g, ''));
          if (!isNaN(parsed)) {
            if (parsed >= 100 && typeof game?.showBigWin === 'function') {
              game?.showBigWin(parsed);
            }
            game?.addCredits(parsed);
          }
        }
      }
      if (this.app.restartGame) {
        setTimeout(() => this.app.restartGame(), 6000);
      }
    } else { // API result is "lost" or undefined/error (e.g., "pending" for individual scratches)
      // Only show "Try Again" and restart if all covers are revealed.
      if (this.allCoversRevealed() && this.app.buttonManager && typeof this.app.buttonManager.showTryAgain === 'function') {
        this.app.buttonManager.showTryAgain();
        if (soundManager && typeof soundManager.playtryagain === 'function') {
          soundManager.playtryagain();
        }
        setTimeout(() => {
          if (this.app.restartGame) {
            this.app.restartGame();
          }
        }, 2000);
      }
    }
  }
  
  // Highlight matching symbols and dim others for 3 seconds
  highlightMatches(matchedGridIndices, matchedMatchIndices) {
    // Highlight effect: scale up and tint
    const isMatchedGrid = (index) => matchedGridIndices.includes(index);

    const isMatchedMatch = (index) => (index === 9 && matchedMatchIndices.includes(0)) || (index === 10 && matchedMatchIndices.includes(1));
    const highlight = (container) => {
      if (container.actualSymbol) {
        container.actualSymbol.tint = 0xffff00;
        if (container.actualSymbol._originalScale) {
          container.actualSymbol.scale.set(
            container.actualSymbol._originalScale.x * 1.2,
            container.actualSymbol._originalScale.y * 1.2
          );
        }
      }
    };
    // Remove highlight
    const removeHighlight = (container) => {
      if (container.actualSymbol) {
        container.actualSymbol.tint = 0xffffff;
        if (container.actualSymbol._originalScale) {
          container.actualSymbol.scale.set(
            container.actualSymbol._originalScale.x,
            container.actualSymbol._originalScale.y
          );
        }
      }
    };
    this.symbols.forEach((container, idx) => {
      if (container.isRevealed) { // Only apply effects to symbols that are actually revealed
        const isCurrentSymbolMatch = (idx < 9 && isMatchedGrid(idx)) || (idx >= 9 && isMatchedMatch(idx));
        if (isCurrentSymbolMatch) {
          highlight(container); // Apply highlight effect
          container.alpha = 1; // Ensure full opacity for highlighted matches
        } else {
          removeHighlight(container); // Reset visuals for non-matches
          container.alpha = 0.3; // Dim non-matching revealed symbols
        }
      } else {
        // If a symbol is NOT revealed, ensure it's not visually affected by highlight logic
        // Covers will remain at full alpha and handle their own visibility
        removeHighlight(container);
        container.alpha = 1;
      }
    });
    // After 3 seconds, remove all highlights and restore full alpha to all revealed symbols
    setTimeout(() => {
      this.symbols.forEach((container) => {
        if (container.isRevealed) { // Only reset revealed symbols
          removeHighlight(container);
          container.alpha = 1;
        }
      });
    }, 3000);
  }

  // Patch: call checkAndHighlightWin after each cover is revealed

  updateLanguage() {
    // Update matching icons text with appropriate font size
    if (this.leftMatchingText) {
      const currentLanguage = languageManager.getCurrentLanguage();
      const screenWidth = this.app.screen.width;
      let baseFontSize = 28;
      
      if (screenWidth <= 480) {
        baseFontSize = 20; // Increased from 14 to 20 for better visibility
      } else if (screenWidth <= 767) {
        baseFontSize = 24; // Increased from 18 to 24 for better visibility
      } else if (screenWidth <= 1024) {
        baseFontSize = 26; // Increased from 22 to 26 for better visibility
      }
      
      const fontSize = this.getMatchingIconsFontSize(currentLanguage, baseFontSize);
      this.leftMatchingText.text = languageManager.getText('matchingIcons');
      this.leftMatchingText.style.fontSize = fontSize;
    }
    if (this.rightMatchingText) {
      const currentLanguage = languageManager.getCurrentLanguage();
      const screenWidth = this.app.screen.width;
      let baseFontSize = 28;
      
      if (screenWidth <= 480) {
        baseFontSize = 20; // Increased from 14 to 20 for better visibility
      } else if (screenWidth <= 767) {
        baseFontSize = 24; // Increased from 18 to 24 for better visibility
      } else if (screenWidth <= 1024) {
        baseFontSize = 26; // Increased from 22 to 26 for better visibility
      }
      
      const fontSize = this.getMatchingIconsFontSize(currentLanguage, baseFontSize);
      this.rightMatchingText.text = languageManager.getText('matchingIcons');
      this.rightMatchingText.style.fontSize = fontSize;
    }
  }

  /**
   * Get appropriate font size for matching icons text based on language
   * @param {string} language - Language code (en, fr, it)
   * @param {number} baseFontSize - Base font size for the current screen size
   * @returns {number} Font size
   */
  getMatchingIconsFontSize(language, baseFontSize = 28) {
    switch (language) {
      case 'en':
        return baseFontSize; // English: use base size
      case 'fr':
        return baseFontSize * 0.8; // French: 20% smaller due to longer text
      case 'it':
        return baseFontSize * 0.7; // Italian: 30% smaller due to longest text
      default:
        return baseFontSize; // Fallback to base size
    }
  }
}